/**
 * The Patient Panel's action gates — Patient Profile's OWN rules, for a component without the mixin
 * (Schedule View Phase 8, 2026-09-25).
 *
 * Nilay's decision (2026-09-25): an action the panel can't offer looks exactly as it does on Patient
 * Profile — disabled, with the same reason popover and, for a feature the office hasn't paid for,
 * the same upsell card. So every field returned by resolveActionGates() mirrors the PatientProfile.vue
 * computed OF THE SAME NAME (canAccessTxPresenter / patientProfilePhotoEnabled / isDemoModeOn mirror
 * src/mixin.js): same inputs, same order of checks, same sentences, built on the same shared rules
 * (textConsent, patientPhone, siteCapability, patientPhotoGate). Their quirks are kept on purpose —
 * e.g. canSendEmail is true for an Email that is null rather than '' — because the point is that
 * the two screens never disagree about a patient.
 *
 * ⛔ PatientProfile.vue is not edited (shared-branch rule), so the two copies are held together by
 * tests/component/PatientPanelActionGates.spec.js, which runs PatientProfile's real computeds and
 * this file over the same matrix and fails on any difference. Change a rule THERE first.
 *
 * The upsell / "unavailable" card copy is Patient Profile's template text, pinned verbatim by
 * cypress/node-tests/patient-panel-actions.test.js.
 */
import { isEmailEnabled, isSmsEnabled, hasSmsNumber, SMS_DISABLED_MESSAGE } from '@/utils/siteCapability.js';
import { isTextablePhone, describePhoneProblem } from '@/utils/patientPhone.js';
import { getTextBlockReason, isTextBlocked, TEXT_BLOCK_CODES } from '@/utils/textConsent.js';
import { isPatientPhotoEnabled } from '@/utils/patientPhotoGate.js';

/**
 * The mixin's `allSettings`: the store's settings array keyed by Property (last row wins, as
 * lodash keyBy does). An already-keyed object passes through.
 */
export function keySettings(settings) {
  if (!settings) return null;
  if (!Array.isArray(settings)) return settings;
  const out = {};
  settings.forEach((s) => { if (s && s.Property) out[s.Property] = s; });
  return out;
}

function propertyValue(allSettings, key) {
  const row = allSettings && allSettings[key];
  return row ? row.PropertyValue : undefined;
}

/**
 * @param {object}  args
 * @param {object}  args.allSettings    settings keyed by Property (see keySettings)
 * @param {object}  args.patient        Patient Profile's `patientData` shape (OD column names), or null
 * @param {object}  args.site           the teamio.sites row (customer/getSite), or null
 * @param {boolean} args.isSuperAdmin
 * @param {boolean} args.canViewReviews $can('view', 'reviews')
 * @param {boolean} args.maskOn         the demo mask (phiMaskState) — user OR site switch
 */
export function resolveActionGates(args) {
  const { allSettings, patient, site, isSuperAdmin, canViewReviews, maskOn } = args || {};
  const s = allSettings || null;
  const p = patient || null;

  const smsOptOutTimezone = propertyValue(s, 'GENERAL_TIMEZONE') || '';

  const formsPaidFor = propertyValue(s, 'FORMS_PAID_FOR') === 'TRUE';

  // Two questions (siteCapability): smsFeatureEnabled = the office HAS texting (else: upsell);
  // smsSendable = it may SEND one. They differ only at a WhatsApp-only office
  // (SMS_OUTBOUND_DISABLED), which has a number but must get a reason, never an upsell.
  const smsFeatureEnabled = hasSmsNumber(s);
  const smsSendable = isSmsEnabled(s);

  const emailFeatureEnabled = isEmailEnabled(s);

  const canSendForms = formsPaidFor && (smsSendable || emailFeatureEnabled);

  let canSendSMS = false;
  if (smsSendable) {
    canSendSMS = isTextBlocked({ patient: p, timezone: smsOptOutTimezone })
      ? false
      : isTextablePhone(p && p.WirelessPhone);
  }

  const canSendEmail = isEmailEnabled(s) ? !!(p && p.Email !== '') : false;

  const smsDisabledReasons = [];
  if (!smsFeatureEnabled) {
    smsDisabledReasons.push({ icon: 'fa-cog', text: 'SMS feature is not enabled on your account yet', isFeatureIssue: true });
  } else if (!smsSendable) {
    smsDisabledReasons.push({ icon: 'fa-ban', text: SMS_DISABLED_MESSAGE, isFeatureIssue: true });
  } else {
    const block = getTextBlockReason({ patient: p, timezone: smsOptOutTimezone });
    if (block.blocked) {
      smsDisabledReasons.push({ icon: 'fa-user-times', text: block.message, isFeatureIssue: false });
    } else {
      const phoneProblem = describePhoneProblem(p && p.WirelessPhone);
      if (phoneProblem) smsDisabledReasons.push({ icon: 'fa-phone', text: phoneProblem, isFeatureIssue: false });
    }
  }

  let smsDisabledTip;
  const tipBlock = getTextBlockReason({ patient: p, timezone: smsOptOutTimezone });
  if (smsFeatureEnabled && !smsSendable) {
    smsDisabledTip = null; // nothing in the patient's record would change it
  } else if (tipBlock.blocked) {
    smsDisabledTip = tipBlock.code === TEXT_BLOCK_CODES.OPTED_OUT_BY_TEXT
      ? null
      : "Change the patient's text message setting in Open Dental to enable SMS.";
  } else {
    smsDisabledTip = 'Update patient contact info in Open Dental to enable SMS.';
  }

  const emailDisabledReasons = [];
  if (!emailFeatureEnabled) {
    emailDisabledReasons.push({ icon: 'fa-cog', text: 'Email feature is not configured on your account yet', isFeatureIssue: true });
  } else if (!p || p.Email === '') {
    emailDisabledReasons.push({ icon: 'fa-user', text: 'Patient does not have an email address in Open Dental', isFeatureIssue: false });
  }

  const formsDisabledReasons = [];
  if (!formsPaidFor) {
    formsDisabledReasons.push({ icon: 'fa-credit-card', text: 'Forms feature has not been purchased for your account', isFeatureIssue: true });
  }
  if (formsPaidFor && !smsSendable && !emailFeatureEnabled) {
    formsDisabledReasons.push({
      icon: 'fa-ban',
      text: 'Neither SMS nor Email is configured for your account. At least one delivery method is required to send forms.',
      isFeatureIssue: true,
    });
  } else if (formsPaidFor) {
    if (!smsSendable && emailFeatureEnabled) {
      formsDisabledReasons.push({ icon: 'fa-info-circle', text: smsFeatureEnabled ? 'Texting is turned off for this office. Forms can only be sent via Email.' : 'SMS is not configured. Forms can only be sent via Email.', isFeatureIssue: false });
    } else if (smsSendable && !emailFeatureEnabled) {
      formsDisabledReasons.push({ icon: 'fa-info-circle', text: 'Email is not configured. Forms can only be sent via SMS.', isFeatureIssue: false });
    }
  }

  const canAccessTxPresenter = !!isSuperAdmin || propertyValue(s, 'TX_PRESENTER_ENABLED') === 'TRUE';

  const hasPhone = !!(p && p.WirelessPhone && String(p.WirelessPhone).trim() !== '');
  const hasEmail = !!(p && p.Email && String(p.Email).trim() !== '');
  const reviewRequestPrereqMet = !!p && (hasPhone || hasEmail);

  let showReviewsUpsell;
  if (!s) showReviewsUpsell = false;
  else if (canViewReviews) showReviewsUpsell = false;
  else showReviewsUpsell = propertyValue(s, 'REVIEWS_PAID_FOR') !== 'TRUE';

  const paymentEnabled = propertyValue(s, 'PAYMENT_ENABLED') === '1';

  let hasPaymentProvider = false;
  if (site) {
    if (site.PaymentProvider === 'moolah') hasPaymentProvider = true;
    else if (site.PaymentProvider === 'stripe' && site.StripeAccountId) hasPaymentProvider = true;
  }

  const canCollectPayment = paymentEnabled && hasPaymentProvider;

  const collectPaymentDisabledReasons = [];
  if (!paymentEnabled) {
    collectPaymentDisabledReasons.push({ icon: 'fa-power-off', text: 'Payment collection is turned off for this site.' });
  }
  if (paymentEnabled && !hasPaymentProvider) {
    if (site && site.PaymentProvider === 'stripe' && !site.StripeAccountId) {
      collectPaymentDisabledReasons.push({ icon: 'fa-link', text: 'Stripe Connect setup is incomplete. Reconnect Stripe.' });
    } else {
      collectPaymentDisabledReasons.push({ icon: 'fa-cog', text: 'No payment provider is configured. Pick Moolah or Stripe.' });
    }
  }

  let resolvedPatientBalance = 0;
  if (p) {
    const a = Number(p.PatientAmount);
    const e = Number(p.EstBalance);
    if (!isNaN(a) && a !== 0) resolvedPatientBalance = a;
    else if (!isNaN(e) && e !== 0) resolvedPatientBalance = e;
  }

  return {
    smsOptOutTimezone,
    formsPaidFor,
    smsFeatureEnabled,
    emailFeatureEnabled,
    canSendForms,
    canSendSMS,
    canSendEmail,
    smsDisabledReasons,
    smsDisabledTip,
    emailDisabledReasons,
    formsDisabledReasons,
    canAccessTxPresenter,
    reviewRequestPrereqMet,
    showReviewsUpsell,
    paymentEnabled,
    hasPaymentProvider,
    canCollectPayment,
    collectPaymentDisabledReasons,
    patientProfilePhotoEnabled: isPatientPhotoEnabled(s),
    isDemoModeOn: !!maskOn,
    resolvedPatientBalance,
  };
}

/* ------------------------------------------------------------------------------------------------
 * The buttons, in the design's order, each with Patient Profile's disabled rule and popover content.
 * ---------------------------------------------------------------------------------------------- */

/** Patient Profile's upsell cards (template text, verbatim). `sourcePage` names THIS surface. */
export const UPSELLS = {
  form: {
    feature: 'FORMS', featureLabel: 'Digital Forms', sourcePage: 'Schedule View - Send Form',
    title: 'Enable Digital Forms',
    desc: 'Send intake forms, consent forms, and health history forms directly to patients.',
  },
  text: {
    feature: 'SMS', featureLabel: '2-Way SMS texting', sourcePage: 'Schedule View - SMS',
    title: 'Enable 2-Way SMS',
    desc: 'Send appointment reminders, confirmations, and have real-time conversations with patients.',
  },
  email: {
    feature: 'EMAIL', featureLabel: 'Patient email', sourcePage: 'Schedule View - Email',
    title: 'Enable Email Feature',
    desc: 'Send appointment reminders, confirmations, and follow-ups directly to patients via email.',
  },
  qr: {
    feature: 'FORMS', featureLabel: 'Digital Forms', sourcePage: 'Schedule View - Get Form QR',
    title: 'Enable Digital Forms',
    desc: 'Generate QR codes for in-office form handoff — no patient search needed.',
  },
  txplans: {
    feature: 'TX_PRESENTER', featureLabel: 'Treatment Presenter', sourcePage: 'Schedule View - Tx Plans',
    title: 'Enable Treatment Presenter',
    desc: 'Send treatment plans to patients and collect their signatures online.',
  },
  review: {
    feature: 'REVIEWS', featureLabel: 'Review Requests', sourcePage: 'Schedule View - Review Request',
    title: 'Enable Review Requests',
    desc: 'Ask patients for a Google review by text or email.',
  },
  photo: {
    feature: 'ALL_IN_ONE', featureLabel: 'All-in-One package (Photo to OD)', sourcePage: 'Schedule View - Photo to OD',
    title: 'Upgrade to send photos to Open Dental',
    desc: 'Snap insurance cards, medication lists and other documents on your phone and file them straight into Open Dental Imaging. Included in the All-in-One package. Upgrade your subscription to turn it on.',
  },
};

/** Patient Profile's "… Unavailable" card headers and tips (template text, verbatim). */
export const UNAVAILABLE = {
  form: { title: 'Forms Unavailable', fallback: 'You may need to create Forms before sending to patients' },
  text: { title: 'SMS Unavailable' },
  email: { title: 'Email Unavailable', tip: 'Update patient info in Open Dental to enable email.' },
  review: {
    title: 'Review Request Unavailable',
    reason: 'No mobile number or email on file for this patient.',
    tip: 'Add a phone or email in Open Dental to send review requests.',
  },
  pay: { title: 'Collect Payment Unavailable', tip: 'Open Settings → Payment to fix.' },
  photoMask: 'Turned off while the demo mask is on',
};

/**
 * Every action the bar shows, in the design's order, with its state. `why` is what the disabled
 * popover shows: an upsell card, or an "unavailable" card of reasons. `hidden` actions render
 * nothing — a paid site whose role can't use Reviews sees no Review button, as on Patient Profile.
 *
 * @param {object} gates           resolveActionGates()
 * @param {object} state
 * @param {boolean} state.canViewReviews
 * @param {boolean} state.txLoading       the Tx Plans dispatcher is loading
 * @param {boolean} state.paymentOpening  Collect Payment is fetching its settings
 * @param {boolean} state.followupReady   the host loaded the follow-up rows and can start one (the
 *                                        Patient Profile page). Absent ⇒ Follow-up stays "coming soon".
 */
export function buildActions(gates, state) {
  const g = gates;
  const st = state || {};
  const unavailable = (title, reasons, tip, fallback) => ({ unavailable: { title, reasons: reasons || [], tip: tip || null, fallback: fallback || null } });

  const form = !g.canSendForms
    ? (!g.formsPaidFor ? { upsell: UPSELLS.form } : unavailable(UNAVAILABLE.form.title, g.formsDisabledReasons, null, UNAVAILABLE.form.fallback))
    : null;
  const text = !g.canSendSMS
    ? (!g.smsFeatureEnabled ? { upsell: UPSELLS.text } : unavailable(UNAVAILABLE.text.title, g.smsDisabledReasons, g.smsDisabledTip))
    : null;
  const email = !g.canSendEmail
    ? (!g.emailFeatureEnabled ? { upsell: UPSELLS.email } : unavailable(UNAVAILABLE.email.title, g.emailDisabledReasons, UNAVAILABLE.email.tip))
    : null;

  let review;
  if (st.canViewReviews) {
    review = {
      key: 'review', label: 'Review request', short: 'Review', icon: 'fa-star',
      enabled: g.reviewRequestPrereqMet,
      why: g.reviewRequestPrereqMet ? null : unavailable(UNAVAILABLE.review.title, [{ icon: 'fa-address-book', text: UNAVAILABLE.review.reason }], UNAVAILABLE.review.tip),
    };
  } else if (g.showReviewsUpsell) {
    review = { key: 'review', label: 'Review request', short: 'Review', icon: 'fa-star', enabled: false, why: { upsell: UPSELLS.review } };
  } else {
    review = { key: 'review', label: 'Review request', short: 'Review', icon: 'fa-star', enabled: false, hidden: true, why: null };
  }

  const photoOk = g.patientProfilePhotoEnabled && !g.isDemoModeOn;
  let photoWhy = null;
  if (!g.patientProfilePhotoEnabled) photoWhy = { upsell: UPSELLS.photo };
  else if (g.isDemoModeOn) photoWhy = unavailable('Photo to OD', [{ icon: 'fa-eye-slash', text: UNAVAILABLE.photoMask }]);

  return [
    { key: 'followup', label: 'Follow-up', short: 'Follow-up', icon: 'fa-bell-o', enabled: !!st.followupReady, primary: true, soon: !st.followupReady, why: null },
    { key: 'form', label: 'Send form', short: 'Form', icon: 'fa-file-text-o', group: 'send', enabled: g.canSendForms, why: form },
    { key: 'text', label: 'Send text', short: 'Text', icon: 'fa-comment-o', group: 'send', enabled: g.canSendSMS, why: text },
    { key: 'email', label: 'Send email', short: 'Email', icon: 'fa-envelope-o', group: 'send', enabled: g.canSendEmail, why: email },
    {
      key: 'txplans', label: 'Tx plans', short: 'Tx Plans', icon: 'fa-stethoscope',
      enabled: g.canAccessTxPresenter && !st.txLoading, busy: !!st.txLoading,
      why: g.canAccessTxPresenter ? null : { upsell: UPSELLS.txplans },
    },
    { key: 'task', label: 'Create task', short: 'Create Task', icon: 'fa-check-square-o', enabled: true, why: null },
    review,
    { key: 'photo', label: 'Photo to OD', short: 'Photo to OD', icon: 'fa-camera', enabled: photoOk, why: photoWhy },
    { key: 'qr', label: 'Get form QR', short: 'Form QR', icon: 'fa-qrcode', enabled: g.formsPaidFor, why: g.formsPaidFor ? null : { upsell: UPSELLS.qr } },
    {
      key: 'pay', label: 'Collect payment', short: 'Payment', icon: 'fa-dollar',
      enabled: g.canCollectPayment && !st.paymentOpening, busy: !!st.paymentOpening,
      why: g.canCollectPayment ? null : unavailable(UNAVAILABLE.pay.title, g.collectPaymentDisabledReasons, UNAVAILABLE.pay.tip),
    },
  ];
}
